import {FormattedMessage} from "react-intl";

import * as yup from "yup";
import "yup-phone-lite";

import {DeliveryModesRequiringShopName, ShippingMethodsDiscr} from "utils/ShippingMethods";

import {MetadataTypes, managedAdditionalDataKeys} from "./utils/metadata";

const regexPhoneNumber = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

export default function getValidationSchema(isWaybill, isReturnWaybill, shippingMethods, isEditing, discr) {
    return yup.object({
        name: createRequiredStringValidation('name'),
        shopName: createRequiredStringValidation('shopName').test('testShopName',  (value, {path, createError}) => {
            if (isEditing) {
                return true;
            }

            const alreadyExist = shippingMethods?.some(shippingMethod => shippingMethod.shopName?.toLowerCase() === value?.toLowerCase());

            if (alreadyExist) {
                return createError({
                    message: <FormattedMessage id={`shippingMethods.form.field.${path}.error.alreadyExist`}/>
                });
            }

            return true;
        }),
        shippingMethodShopName: createRequiredStringValidation('shippingMethodShopName'),
        icon: yup.string().nullable(),
        carrierIcon: yup.string().nullable(),
        carrier: yup.string().nullable(),
        deliveryMode: createRequiredStringValidation('deliveryMode').test('testDeliveryModeShopName', (value, {path, parent, createError}) => {
            // mirrors Sylius' rule both ways, or the push would be rejected
            const shopName = parent.shopName?.trim();
            const modeRequiresShopName = DeliveryModesRequiringShopName.includes(value) && value !== shopName;
            const shopNameRequiresMode = DeliveryModesRequiringShopName.includes(shopName) && value !== shopName;

            if (modeRequiresShopName || shopNameRequiresMode) {
                return createError({
                    message: <FormattedMessage id={`shippingMethods.form.field.${path}.error.shopName`} values={{shopName: shopNameRequiresMode ? shopName : value}}/>
                });
            }

            return true;
        }),
        minimumEstimatedDeliveryDuration: createDurationValidation().test('testMinimumBelowMaximum', (value, {path, parent, createError}) => {
            const maximum = parent.maximumEstimatedDeliveryDuration;

            if (isBlank(value) || isBlank(maximum) || parseInt(value, 10) <= parseInt(maximum, 10)) {
                return true;
            }

            return createError({
                message: <FormattedMessage id={`shippingMethods.form.field.${path}.error.aboveMaximum`}/>
            });
        }),
        maximumEstimatedDeliveryDuration: createDurationValidation(),
        returnOnly: yup.bool(),
        checkoutName: yup.string().nullable().max(255),
        trackingUrl: yup.string().nullable().max(255).test('testTrackingUrl', (value, {path, createError}) => {
            if (isBlank(value) || /^https?:\/\//.test(value.trim())) {
                return true;
            }

            return createError({
                message: <FormattedMessage id={`shippingMethods.form.field.${path}.error.notValid`}/>
            });
        }),
        discr: createRequiredStringValidation('discr'),
        skipReservedStep: yup.bool(),
        isDefaultReturnMethod: yup.bool(),
        secureService: yup.bool(),
        senderName: createConditionalFieldValidation('testSenderName', isWaybill),
        contactMailAddress: createConditionalFieldValidation('testContactMailAddress', isWaybill, 'email'),
        contactPhoneNumber: createConditionalFieldValidation('testContactPhoneNumber', isWaybill, 'phone'),
        senderAddress: createConditionalFieldValidation('testSenderAddress', isWaybill),
        senderCity: createConditionalFieldValidation('testSenderCity', isWaybill),
        senderCityZipCode: createConditionalFieldValidation('senderCityZipCode', isWaybill)
            .test('senderCityZipCodeFormat', (value, {path, createError}) => {
                if (!value) return true;
                if (value.length < 5) {
                    return createError({
                        message: <FormattedMessage id={`shippingMethods.form.field.${path}.error.minLength`} />
                    });
                }
                if (!/^\d+$/.test(value)) {
                    return createError({
                        message: <FormattedMessage id={`shippingMethods.form.field.${path}.error.notInteger`} />
                    });
                }
                return true;
            }),
        senderCountryCode: createConditionalFieldValidation('senderCountryCode', isWaybill)
            .test('senderCountryCodeFormat', (value, {path, createError}) => {
                if (!value) return true;
                if (value.length !== 2) {
                    return createError({
                        message: <FormattedMessage id={`shippingMethods.form.field.${path}.error.exactLength`} />
                    });
                }
                if (!/^[A-Z]+$/.test(value)) {
                    return createError({
                        message: <FormattedMessage id={`shippingMethods.form.field.${path}.error.notUppercase`} />
                    });
                }
                return true;
            }),
        contractNumber: createConditionalFieldValidation('testContractNumber', isWaybill && ShippingMethodsDiscr.DPD !== discr),
        wsPassword: createConditionalFieldValidation('testWsPassword', isWaybill),
        userId: createConditionalFieldValidation('testUserId', ShippingMethodsDiscr.DPD === discr),
        customerNumber: createConditionalFieldValidation('testCustomerNumber', ShippingMethodsDiscr.DPD === discr, 'number'),
        customerCenterNumber: createConditionalFieldValidation('testCustomerCenterNumber', ShippingMethodsDiscr.DPD === discr, 'number'),
        printConfig: yup.lazy(value =>
            Array.isArray(value)
                ? yup.array().of(yup.object().shape({})) // Allow empty array
                : yup.object().shape({
                    returnShippingMethodOverride: isReturnWaybill
                        ? yup.string().required('shippingMethods.form.field.printConfig.returnShippingMethodOverride.error.required')
                        : yup.string().nullable(),
                })
        ),
        additionalData: yup.lazy(value =>
            Array.isArray(value)
                ? yup.array().of(yup.object().shape({})) // Allow empty array
                : yup.object().shape({
                    sandbox: yup.bool(),
                    customerNumber: createConditionalAdditionalDataFieldValidation('testCustomerNumber', [ShippingMethodsDiscr.POSTNL, ShippingMethodsDiscr.NSHIFT]),
                    carrierServiceId: createConditionalAdditionalDataFieldValidation('testCarrierServiceId', [ShippingMethodsDiscr.NSHIFT]),
                    returnCarrierServiceId: createConditionalAdditionalDataFieldValidation('testReturnCarrierServiceId', [ShippingMethodsDiscr.NSHIFT]),
                    api_url: createConditionalAdditionalDataFieldValidation('testApiUrl', [ShippingMethodsDiscr.GEODIS]),
                    api_username: createConditionalAdditionalDataFieldValidation('testApiUsername', [ShippingMethodsDiscr.GEODIS]),
                    api_key: createConditionalAdditionalDataFieldValidation('testApiKey', [ShippingMethodsDiscr.GEODIS]),
                    agency_code: createConditionalAdditionalDataFieldValidation('testAgencyCode', [ShippingMethodsDiscr.GEODIS]),
                    client_code: createConditionalAdditionalDataFieldValidation('testClientCode', [ShippingMethodsDiscr.GEODIS]),
                    product_code: createConditionalAdditionalDataFieldValidation('testProductCode', [ShippingMethodsDiscr.GEODIS]),
                    sender_name: createConditionalAdditionalDataFieldValidation('testApiSenderName', [ShippingMethodsDiscr.GEODIS]),
                    sender_country_code: createConditionalAdditionalDataFieldValidation('testSenderCountryCode', [ShippingMethodsDiscr.GEODIS]),
                    sender_post_code : createConditionalAdditionalDataFieldValidation('testPostCode', [ShippingMethodsDiscr.GEODIS]),
                    sender_city: createConditionalAdditionalDataFieldValidation('testSenderCity', [ShippingMethodsDiscr.GEODIS]),
                }),
        ),
        metadata: yup.array().of(
            yup.object().shape({
                key: createMetadataKeyValidation(),
                type: yup.string().oneOf(Object.values(MetadataTypes)),
                value: createMetadataValueValidation(),
            })
        ),
    });
}

function createMetadataKeyValidation() {
    return yup.string().test(
        'testMetadataKey',
        (value, {createError, from}) => {
            const key = value?.trim() ?? '';

            if (key === '') {
                return createError({
                    message: <FormattedMessage id={'shippingMethods.form.field.metadata.key.error.required'} />
                });
            }

            if (managedAdditionalDataKeys.includes(key)) {
                return createError({
                    message: <FormattedMessage id={'shippingMethods.form.field.metadata.key.error.reserved'} />
                });
            }

            // https://github.com/jquense/yup/issues/225
            // from[0] is the metadata row, from[1] the whole form values
            const metadata = from[1].value.metadata ?? [];

            if (metadata.filter(item => item.key?.trim() === key).length > 1) {
                return createError({
                    message: <FormattedMessage id={'shippingMethods.form.field.metadata.key.error.duplicated'} />
                });
            }

            return true;
        }
    );
}

function createMetadataValueValidation() {
    return yup.string().test(
        'testMetadataValue',
        (value, {createError, from}) => {
            const type = from[0].value.type;

            if (type === MetadataTypes.NUMBER && (!value || isNaN(Number(value)))) {
                return createError({
                    message: <FormattedMessage id={'shippingMethods.form.field.metadata.value.error.notANumber'} />
                });
            }

            if (type === MetadataTypes.JSON) {
                try {
                    JSON.parse(value);
                } catch (e) {
                    return createError({
                        message: <FormattedMessage id={'shippingMethods.form.field.metadata.value.error.notAJson'} />
                    });
                }
            }

            return true;
        }
    );
}

function isBlank(value) {
    return value === null || value === undefined || String(value).trim() === '';
}

// a number of days, optional
function createDurationValidation() {
    return yup.string().nullable().test('testDuration', (value, {path, createError}) => {
        if (isBlank(value) || /^\d+$/.test(String(value).trim())) {
            return true;
        }

        return createError({
            message: <FormattedMessage id={`shippingMethods.form.field.${path}.error.notValid`}/>
        });
    });
}

function createRequiredStringValidation(name) {
    return yup.string().required(<FormattedMessage id={`shippingMethods.form.field.${name}.error.required`}/>);
}

function createConditionalAdditionalDataFieldValidation(testName, methods) {
    return yup.string().nullable().test(
        testName,
        (value, {path, createError, from}) => {
            // https://github.com/jquense/yup/issues/225
            // when we are on nested object, we cant use .when() to access parent values like discr
            const discr = from[1].value.discr;

            // validation fail
            if (methods.includes(discr) && (value === null || !value || value.trim() === '')) {
                return createError({
                    message: <FormattedMessage id={`shippingMethods.form.field.${path}.error.required`} />
                });
            }

            // validation pass
            return true;
        }
    );
}

function createConditionalFieldValidation(testName, condition, type = 'string') {
    let customYup = yup.string();

    if (type === 'email') {
        customYup = yup.string().email()
    }

    if (type === 'phone') {
        customYup = yup.string().matches(regexPhoneNumber, ({path}) => {
            return <FormattedMessage id={`shippingMethods.form.field.${path}.error.notValid`} />
        })
    }

    if (type === 'number') {
        customYup = yup.string().matches(/^-?\d+$/, ({path}) => {
            return <FormattedMessage id={`shippingMethods.form.field.${path}.error.notValid`} />
        })
    }

    return customYup.nullable().test(
        testName,
        (value, {path, createError}) => {
            // validation fail
            if (condition === true && (value === null || !value || value.trim() === '')) {
                return createError({
                    message: <FormattedMessage id={`shippingMethods.form.field.${path}.error.required`} />
                });
            }

            // validation pass
            return true;
        }
    );
}


